import { Mail, Phone, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import { COMPANY, COLORS } from "@/lib/site";
import { Reveal } from "@/components/Reveal";

const mailtoHref = `mailto:${COMPANY.email}?subject=${encodeURIComponent(
  "Enquiry from aumonline.in"
)}`;
const whatsappHref = `https://wa.me/${COMPANY.phoneRaw}?text=${encodeURIComponent(
  "Hi Aumonline, I have an enquiry."
)}`;

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <Reveal>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A]">Contact us</h1>
            <p className="mt-5 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Have a question about a product or our mutual fund services? Reach us directly — we
              reply quickly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#FDFCF0]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="space-y-4">
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 bg-white rounded-md border border-gray-200 p-6 transition-colors duration-150 hover:border-[#00843D]" data-testid="contact-whatsapp">
                <div className="w-11 h-11 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: COLORS.green }}>
                  <MessageCircle size={22} />
                </div>
                <div>
                  <div className="font-semibold text-[#1A1A1A]">Chat on WhatsApp</div>
                  <div className="text-slate-600 text-sm mt-0.5">The fastest way to reach us — tap to open a chat.</div>
                  <div className="mt-1 text-sm font-medium inline-flex items-center gap-1" style={{ color: COLORS.green }}>Open WhatsApp <ArrowRight size={14} /></div>
                </div>
              </a>

              <a href={mailtoHref} className="flex items-start gap-4 bg-white rounded-md border border-gray-200 p-6 transition-colors duration-150 hover:border-[#0033A0]" data-testid="contact-email">
                <div className="w-11 h-11 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: COLORS.blue }}>
                  <Mail size={22} />
                </div>
                <div>
                  <div className="font-semibold text-[#1A1A1A]">Email us</div>
                  <div className="text-slate-600 text-sm mt-0.5">Tap to write to us — opens your email app.</div>
                  <div className="mt-1 text-sm font-medium" style={{ color: COLORS.blue }}>{COMPANY.email}</div>
                </div>
              </a>

              <a href={`tel:+${COMPANY.phoneRaw}`} className="flex items-start gap-4 bg-white rounded-md border border-gray-200 p-6 transition-colors duration-150 hover:border-[#0033A0]" data-testid="contact-phone">
                <div className="w-11 h-11 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: COLORS.blue }}>
                  <Phone size={22} />
                </div>
                <div>
                  <div className="font-semibold text-[#1A1A1A]">Call us</div>
                  <div className="text-slate-600 text-sm mt-0.5">Tap to call during business hours.</div>
                  <div className="mt-1 text-sm font-medium" style={{ color: COLORS.blue }}>{COMPANY.phoneDisplay}</div>
                </div>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-white rounded-md border border-gray-200 p-6 md:p-8 h-full">
              <div className="flex items-center gap-3">
                <MapPin size={22} style={{ color: COLORS.blue }} />
                <div className="font-semibold text-[#1A1A1A] text-lg">Registered office</div>
              </div>
              <address className="mt-4 not-italic text-slate-700 leading-relaxed">
                <div className="font-semibold text-[#1A1A1A]">{COMPANY.displayName}</div>
                <div className="mt-1">{COMPANY.addressLine1}</div>
                <div>{COMPANY.addressLine2}</div>
              </address>
              <div className="mt-6 rounded-md overflow-hidden border border-gray-200">
                <iframe
                  title="Aumonline location — Yadavgiri, Mysuru"
                  src="https://www.google.com/maps?q=Yadavgiri%2C%20Mysuru%2C%20Karnataka%20570020&output=embed"
                  className="w-full h-56"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                For privacy-related questions, email {COMPANY.email}. Aumonline is an
                AMFI-registered Mutual Fund Distributor ({COMPANY.arn}).
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
